export const current = `
These Terms of Service ("Terms of Service" or "Terms"), together with an Order Form, govern a customer's use
of [https://hightouch.com](https://hightouch.com) (the "Site") owned and operated by Carry Technologies, Inc. dba Hightouch, ("Hightouch", "our", "us" or
"we"). "You", "your" or "Customer" means the entity that uses the Site. Customer and Hightouch may be
referred to in these Terms as a "Party" and collectively as the "Parties."

By signing an Order Form (defined below) with Hightouch, Customer agrees to comply with and be bound by
these Terms. These Terms, together with any Order Form(s), constitutes the entire agreement between
Hightouch and Customer (the "Agreement").

## 1.0 Definitions

**1.1.** "Affiliate." Any business entity controlling, controlled by or under common control with a Party hereunder.
Customer will be responsible for all acts and omissions of its Affiliates, as if they were "Customer"
hereunder.

**1.2.** "Confidential Information" means (i) the Agreement, (ii) with respect to Hightouch, non-public aspects of
the Hightouch Technology, the Site, and additional services provided by Hightouch (and the operation
thereof), and Hightouch's business, technical or financial information and data, (iii) with respect to
Customer, Customer Data, and non-public aspects of Customer's technology, computer programs, and business,
technical and financial information and data.

**1.3.** "Customer Data." Non-public data provided by Customer to Hightouch to enable the provision of the Site and
other services, including protected health information, as defined in the Health Insurance Portability and
Accountability Act of 1996 (as amended, "HIPAA").

**1.4.** "Destination." A type of application connected to the Site by Customer to which the Site sends data.
Multiple instances of one destination count as one Destination. For example, if Customer connects two
Salesforce accounts, Customer is only billed for one Destination.

**1.5.** "Standard Destination." A Destination listed as Standard on the Site.

**1.6.** "Premium Destination." A Destination listed as Premium on the Site.

**1.7.** "Paid Destination." A Destination listed as Standard or Premium on the Site.

**1.8.** "Free Destination." A Destination listed as Free on the Site.

**1.9.** "Event." A record that the Site queries in a Source via a Sync and inserts (but does not update) in the
Destination.

**1.10.** "Hightouch Technology." The computer hardware, software and other tangible equipment and intangible computer
code necessary to deploy and make available the Site.

**1.11.** "Monthly Queried Records" or "MQR." The number of unique Records to be queried in a Source in a month.

**1.12.** "Object." A record that the Site queries in a Source via a Sync and is then updates in a Destination.

**1.13.** "Operation." An operation is one of an addition, deletion, or modification executed by the Site to a
Destination. An addition, deletion, or modification typically consists of one or more application
programming interfaces (API) calls made from the Site to a Destination.

**1.14.** "Order Form." Any order form referencing and incorporating these Terms, which shall be in writing, signed by
both Parties, and shall specify the products or services to be purchased or licensed by Customer from
Hightouch.

**1.15.** "Service Term." The period of time indicated in an Order Form beginning upon an Order Effective Date and
ending on the last day of the Service Term set forth in the Order Form.

**1.16.** "Source." A data repository of Customer connected to the Site from which the Site reads data. An example of
a Source is a Snowflake data warehouse or Google Sheet.

**1.17.** "Sync." A process created by Customer in the Site that defines how and when data is moved from Source to
Destination by the Site.

**1.18.** "Users." The number of identifiable unique persons consisting of Customer's personnel and independent
contractors who are authorized to access and use the Site, as specified in the applicable Order Form(s).
Users include Customer's third party contractors, outsourcers, consultants and other service providers,
solely to the extent providing services to Customer.

**1.19.** "Third Party Systems." means a third party's web-based, mobile, or other software application that
interoperates with the Site and is made available by Customer or a third party, including without limitation
any Destination or Source.

## 2.0 Access to Services

Subject to the terms and conditions hereof, during the Service Term set forth in the applicable Order Form,
Hightouch hereby grants to Customer a non-exclusive, non-sublicensable right to access and use the Site for
the number of Users and the Destinations and Objects set forth on the applicable Order Form and solely for
Customer's internal business purposes.

## 3.0 Use Restrictions

Customer shall not, nor shall it permit or assist others to,

(i) abuse or fraudulently use the Site; 

(ii) process or permit to be processed the data of any third party that is not expressly authorized herein
to access and use the Site;

(iii) attempt to copy, reverse-engineer, decompile, disassemble, create a derivative work from, or
otherwise attempt to derive the source code of any part of the Site or the Hightouch Technology;

(iv) access, alter, or destroy any information of any customer of Hightouch by any fraudulent means or
device, or attempt to do so; or

(v) use the Site in violation of any applicable law or regulation or any of its agreements with third
parties, or otherwise outside of the scope expressly permitted herein.

In the event of any breach by Customer of any of the foregoing, in addition to any other remedies available
at law or in equity, Hightouch will have the right to immediately suspend Customer's access to the Site if
deemed reasonably necessary by Hightouch to prevent any harm to Hightouch or its business. Hightouch will
provide notice to Customer and an opportunity to cure, if practicable, depending on the nature of the
breach. Once cured, Hightouch will promptly restore access to the Site.

## 4.0 Customer Responsibilities; Third Party Systems

Customer shall be solely responsible for (a) providing all Internet access, hardware, browsers, and other
software necessary to access and login to the Site, and (b) acquiring and maintaining technology and
procedures for maintaining the security of its link to the Internet, its account and passwords, and for all
uses of Customer's account. Customer hereby grants to Hightouch a non-exclusive, worldwide, royalty-free
right to process use, copy, store, transmit, modify, create derivative works of and display the Customer
Data during the Service Term, solely to the extent necessary to provide the Site and any related services to
Customer hereunder. The Site contains features designed to interoperate with Third Party Systems. Customer
agrees that its use of any Third Party System and any exchange of Customer Data between Customer and any
Third Party System is solely between Customer and the applicable Third Party System provider. Hightouch does
not warrant or support Third Party Systems, and Hightouch is not responsible for any disclosure,
modification or deletion of Customer Data resulting from access by any Third Party System or its provider.

## 5.0 Site Security

Hightouch shall implement reasonable security procedures consistent with prevailing industry standards to
protect Customer Data processed by the Site from unauthorized access. Hightouch reserves the right to
internally monitor Customer's usage of the Site for the purposes of troubleshooting and maintaining and
improving the Site.

## 6.0 Fees; Taxes

**6.1.** Customer shall pay to Hightouch the fees for access to the Site based upon Destinations, Events, and Objects
selected by Customer as indicated in the Order Form. Customer will pay invoices within thirty (30) days of
the date of invoice, unless otherwise set forth in the Order Form. Any overdue amounts shall be subject to a
late charge of one-half percent (0.5%) per month or the highest rate permitted by applicable law, whichever
is lower, on such invoice until fully paid. Any amounts due hereunder are exclusive of, and Customer shall
be responsible for, all sales taxes, value added taxes, duties, use taxes, withholdings and other
governmental assessments, excluding taxes based on the net income of Hightouch, unless Customer provides to
Hightouch a valid tax-exempt certificate.

**6.2. A La Carte Fees**
In the event Customer's use of the Site exceeds the volume tier of the subscription plan purchased by
Customer, Hightouch will immediately charge additional fees ("A La Carte Fees") at the overage rate
indicated in the Order Form, and the Customer shall be obligated to pay any A La Carte Fees in the next
month. For subscription plans on MQRs, if the number of Operations in a calendar month is greater than the
number equal to the quantity of MQRs in such calendar month multiplied by 10, each and every additional 10
Operations shall be considered an additional MQR for the purposes of calculating A La Carte Fees.

**6.3. Suspension**
If Customer's account is thirty (30) days or more overdue, then, following five (5) business days' written
notice and opportunity to cure, in addition to any of its other rights or remedies, Hightouch may suspend
Customer's access to the Site until such amounts are paid in full.

## 7.0 Ownership

**7.1.** Hightouch

Hightouch retains all right, title and interest in and to the Site, the Hightouch Technology, Hightouch's
Confidential Information, and all improvements, enhancements or modifications thereto (including all
intellectual property rights therein). Any deliverables, concepts or inventions created or developed before
or while providing the Site or any professional services to Customer are not considered a work made for hire
and shall be owned by Hightouch.

**7.2.** Customer

Customer retains all right, title and interest in and to the Customer Data and Customer's Confidential
Information.

**7.3.** Feedback and Resultant Data

Customer may choose to provide Hightouch with suggestions, ideas, enhancement requests, feedback,
recommendations or other information specifically relating to the Site ("Feedback") and if Customer does
provide Hightouch with Feedback, Hightouch shall own all right, title and interest in such Feedback. In
addition, as a result of Customer's use of the Site, Hightouch may derive (a) information by or through
Customer's use of the Site; (b) that is de-identified so that the identification of any individual cannot be
re-identified or derived; and (c) aggregated with other Hightouch customer usage information ("Resultant
Data"). Hightouch shall retain all right, title, and interest in and to the Resultant Data.

## 8.0 Confidential Information

Each Party ("Owner") may disclose Confidential Information to the other Party ("Recipient") pursuant to this
Agreement.

**8.1.** Restrictions on Use and Disclosure

Recipient may use Confidential Information of Owner only for the purposes of the Agreement and shall protect
such Confidential Information from disclosure to others, using the same degree of care used to protect its
own proprietary information of like importance, but in any case, using no less than a reasonable degree of
care. Recipient may disclose Confidential Information received hereunder only to its employees or
contractors who have a need to know for purposes of this Agreement and who are bound by signed, written
agreements to protect the received Confidential Information from unauthorized use and disclosure.

**8.2.** Exclusions

Confidential Information does not include information that:

(i) is in the possession or control of Recipient without restriction at the time of its disclosure
hereunder;

(ii) is, or becomes, publicly known through no wrongful act of Recipient;

(iii) is received by Recipient from a third party free to disclose it without obligation to Owner, or

(iv) is independently developed by Recipient without reference, reliance or incorporation of the Owner's
Confidential Information.

The Recipient may disclose Confidential Information of Owner pursuant to the requirements of a governmental
agency or by operation of law, provided that such Recipient gives Owner written notice thereof as soon as
practicable and reasonably cooperates with Owner to contest such disclosure. In addition, either Party may
confidentially disclose this Agreement to an actual or potential financing source or acquirer.

**8.3.** Destruction of Confidential Information

At any time upon Owner's request, and upon termination of the Agreement, Recipient shall delete Owner's
Confidential Information.

## 9.0 Representations and Warranties; Disclaimer

**9.1.** By Customer

Customer represents and warrants that it has sufficient authority to enter into this Agreement and that it
has sufficient rights in the Customer Data to authorize Hightouch to process, distribute and display the
Customer Data as contemplated by this Agreement, and that the Customer Data does not infringe the rights of
any third party. Customer will promptly inform Hightouch if it has reason to believe that the former
representations and warranties are no longer true.

**9.2.** By Hightouch

Hightouch warrants that it has sufficient authority to enter into this Agreement and that is has not entered
into any other agreement, obligation or restriction which would prohibit the provision of services to
Customer. Hightouch further warrants that the Site will conform to all material operational features as
described in the documentation for the Site ("Product Warranty"). Customer's sole and exclusive remedy for
breach of the Product Warranty shall be the prompt correction of non-conforming portions of the Site at
Hightouch's sole expense.

**9.3.** Disclaimer

EXCEPT FOR THE REPRESENTATIONS AND WARRANTIES SET FORTH IN THIS SECTION 9, HIGHTOUCH AND ITS AFFILIATES,
SUBCONTRACTORS AND THIRD PARTY LICENSORS MAKE NO OTHER REPRESENTATIONS OR WARRANTIES, WHETHER EXPRESS,
IMPLIED OR STATUTORY, INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF INTELLECTUAL PROPERTY. CUSTOMER ACKNOWLEDGES THAT
HIGHTOUCH SHALL NOT BE LIABLE FOR DELAYS, INTERRUPTIONS, SERVICE FAILURES AND OTHER PROBLEMS INHERENT IN USE
OF THE INTERNET AND ELECTRONIC COMMUNICATIONS OR OTHER THIRD PARTY SYSTEMS OUTSIDE THE REASONABLE CONTROL OF
HIGHTOUCH.

## 10.0 Term and Termination

The Agreement will commence on the Effective Date of an Order Form and continue in full force and effect for
the Service Term indicated in such Order Form and will automatically renew for additional periods equal to
the shorter of the same duration as the initial Service Term or twelve (12) months (each of the initial
Service Term and subsequent renewal terms, a "Service Term"), unless either party notifies the other party
of non-renewal at least thirty (30) days prior to the end of the then-current Service Term. Each Party may
terminate this Agreement upon thirty (30) days prior written notice to the other Party if the other Party
materially breaches this Agreement and fails to correct the breach within such notice period. In the event
of any termination, all amounts due under an Order Form for services and access to the Site provided prior
to the date of termination shall be paid within ten (10) days of such termination. All sections of this
Agreement which by their nature should survive termination will survive termination, including but not
limited to Sections 1 (Definitions), 3 (Use Restrictions), 5 (Site Security), 6 (Fees; Taxes), 7
(Ownership), 8 (Confidential Information), 9.3 (Disclaimer), 10 (Term and Termination), 11
(Indemnification), 12 (Limitations) and 14 (Miscellaneous).

## 11.0 Indemnification

**11.1.** Indemnification by Hightouch

Hightouch will defend Customer from and against any third party claims, demands, suits or proceedings
("Third Party Claims") made or brought against Customer by a third party alleging the Site (except for third
party software, including without limitation open source software) infringes any U.S. patent, copyright or
trademark of a third party, or misappropriates such third party's trade secrets. Further, Hightouch will
indemnify and hold Customer harmless against all damages, costs and reasonable attorneys' fees ("Losses")
finally awarded against Customer by a court of competent jurisdiction or an arbitrator, or agreed to in a
written settlement agreement signed by Hightouch, in connection with such Third Party Claims. Hightouch
shall have no liability or obligation if the Third Party Claim arises from (i) any alteration or
modification to the Site requested or implemented by or on behalf of Customer or its Affiliates, (ii) any
combination of the Site by Customer with other technology or data not furnished by Hightouch, or (iii) any
use by Customer of the Site not in accordance with this Agreement. If the Site becomes or Hightouch believes
it may become the subject of a claim of intellectual property infringement, Hightouch, at its option and
expense, may: (i) procure the right for Customer to continue to use the Site; (ii) replace or modify the
Site so as to make it non-infringing; provided, however, that the Site continues to conform to the
specifications provided in the applicable Order Form; or (iii) terminate this Agreement and refund to
Customer the subscription fees paid in advance by Customer for the Site applicable to the remaining portion
of the Service Term following the effective date of termination. This Section sets forth the entire
liability of Hightouch to Customer for any infringement by the Site of any intellectual property right.

**11.2.** Indemnification by Customer

Customer shall defend Hightouch, at Customer's expense, from and against any Third Party Claims made or
brought against Hightouch alleging that the Customer Data, or Customer's use of the Site in violation of
this Agreement, infringes or otherwise violates a third party's property, privacy or other rights, or
violates any applicable law. Further, Customer shall indemnify and hold Hightouch harmless against all
Losses awarded against Hightouch by a court of competent jurisdiction or an arbitrator, or agreed to in a
written settlement agreement signed by Customer, in connection with such Third Party Claims.

**11.3.** Procedure

The foregoing obligations of the indemnifying party are conditioned upon the party seeking indemnification
(a) promptly providing notice to the indemnifying party concerning the existence of an indemnifiable claim
(provided that failure to give prompt notice shall not constitute a waiver of a party's right to
indemnification and shall affect the indemnifying party's obligations under this Agreement only to the
extent that the indemnifying party's rights are materially prejudiced by such failure or delay), and (b)
promptly providing all information and assistance reasonably requested and otherwise full cooperation with
the indemnifying party in defending the claim, and (c) giving the indemnifying party sole control and
authority of the defense and settlement of any indemnifiable claim (provided, however, that any settlement
requiring the party seeking indemnification to admit liability or make any financial payment shall require
such party's prior written consent, not to be unreasonably withheld or delayed.

## 12.0 Limitations

TO THE MAXIMUM EXTENT PERMITTED BY LAW, AND EXCEPT FOR INDEMNITY OBLIGATIONS EXPRESSLY PROVIDED IN SECTION
11 ABOVE, , IN NO EVENT SHALL HIGHTOUCH OR ITS LICENSORS BE LIABLE TO YOU OR ANY THIRD PARTY (A) FOR ANY
INDIRECT, PUNITIVE, SPECIAL, EXEMPLARY, INCIDENTAL, OR CONSEQUENTIAL DAMAGES (INCLUDING BUT NOT LIMITED TO
LOSS OF PROFITS, REVENUE OR INCOME) ARISING FROM OR RELATED TO THIS AGREEMENT OR THE USE OR INABILITY TO USE
THE SITE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES; OR (B) FOR ANY AMOUNT, IN THE AGGREGATE,
EXCEEDING THE FEES PAID AND PAYABLE IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY,
WHETHER BASED ON BREACH OF WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE.

## 13.0 Co-Marketing

Customer agrees to participate in reasonable marketing activities that promote the benefits of the Services
to other potential customers and to use of Customer's name and logo on the Hightouch web site and in
Hightouch promotional materials. Customer agrees that Hightouch may disclose Customer as a customer of
Hightouch.

## 14.0 Miscellaneous

**14.1.** Assignment

Neither Party shall assign this Agreement without the prior written consent of the other Party; provided,
that either party may transfer this Agreement in connection with a merger, consolidation, sale of
substantially all of its assets, without the other party's prior written consent. Any purported assignment
or delegation in violation of this Section will be null and void. No assignment or delegation will relieve
the assigning or delegating Party of any of its obligations hereunder. This Agreement is binding upon and
inures to the benefit of the Parties and their respective permitted successors and assigns.

**14.2.** Governing Law

This Agreement shall be governed by and construed in accordance with the laws of the State of California,
excluding its conflicts of law rules.

**14.3.** Notices

Any notice or communication required or permitted to be given hereunder may be delivered by hand, deposited
with an overnight courier, sent by email or facsimile (provided delivery is confirmed), or U.S. Mail
(registered or certified only), return receipt requested, in each case to the address set forth on the Order
Form or at such other addresses as shall be designated in writing by either Party to the other in accordance
with this Section. Such notice will be deemed to be given when received.

**14.4.** Modifications; Waiver

No amendment to or modification of this Agreement is effective unless it is in writing and signed by an
authorized representative of each Party. No waiver by any Party of any of the provisions hereof will be
effective unless explicitly set forth in writing and signed by the Party so waiving. Except as otherwise set
forth in this Agreement, (i) no failure to exercise, or delay in exercising, any rights, remedy, power, or
privilege arising from this Agreement will operate or be construed as a waiver thereof and (ii) no single or
partial exercise of any right, remedy, power, or privilege hereunder will preclude any other or further
exercise thereof or the exercise of any other right, remedy, power, or privilege.

**14.5.** Severability

If any provision of this Agreement is invalid, illegal, or unenforceable in any jurisdiction, such
invalidity, illegality, or unenforceability will not affect any other term or provision of this Agreement or
invalidate or render unenforceable such term or provision in any other jurisdiction. Upon such determination
that any term or other provision is invalid, illegal, or unenforceable, the Parties shall negotiate in good
faith to modify this Agreement so as to effect their original intent as closely as possible in a mutually
acceptable manner in order that the transactions contemplated hereby be consummated as originally
contemplated to the greatest extent possible.

**14.6.** Entire Agreement

These Terms of Service, together with any Order Form executed by and between you and Hightouch, constitute
the entire agreement between you and us with respect to the Site and supersede all prior or contemporaneous
communications and proposals (whether oral, written, or electronic) between you and us with respect to the
Site. If any provision of the Agreement is found to be unenforceable or invalid, that provision will be
limited or eliminated to the minimum extent necessary so that the Agreement will remain in full force and
effect. The failure of either Party to exercise in any respect any right provided for in the Agreement shall
not be deemed a waiver of any further rights under this Agreement.

**14.7.** Contact Us

If you have any questions regarding these Terms of Service or the Site, please contact us at [hello@hightouch.com](mailto:hello@hightouch.com).
`;
